import { HttpEvent, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { asyncScheduler, forkJoin, from, merge, Observable, of, timer } from 'rxjs';
import {
  catchError,
  delay,
  exhaustMap,
  filter,
  map,
  mergeMap,
  retryWhen,
  scan,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  throttleTime,
  timeout,
  withLatestFrom,
} from 'rxjs/operators';

import { sentryLog } from '@blaze-shared/logger-util';

import {
  FileItem,
  FileItemFromComputer,
  FileResult,
  getUploadState,
  NotificationData,
  ResourceResult,
} from '../../../domain/public-api';
import { getFriendlyFileType, isImage, mapToMimeType } from '../../../util/public-api';
import { UploadApiService, UploadStartResult } from '../../api/upload/upload.service';
import { switchView } from '../navigation/navigation.actions';
import { getActiveView } from '../navigation/navigation.selectors';
import {
  getAcceptedFileExtensions,
  getAccountId,
  getCategory,
  getIsCropForced,
  getIsMultipleSelection,
  getIsSecure,
  getMinimumImageDimensions,
  getProjectId,
} from '../settings/settings.selectors';

import {
  addFilesFromComputer,
  cancelImageEdit,
  confirmFiles,
  confirmUpload,
  errorEncoding,
  errorUploads,
  fileRetry,
  finishEncoding,
  finishUpload,
  prepareFilesFromComputer,
  removeAllFiles,
  removeFile,
  returnFiles,
  revokeUploadConfirmation,
  saveImageEdit,
  setEncodingProgress,
  setUploadFileProgress,
  startEncoding,
  startImageEdit,
  startUpload,
  startUploadTry,
  triggerNotifications,
  undoImageEdit,
  uploadFile,
  uploadFinished,
  waitForUpload,
  waitForUploadError,
} from './files.actions';
import {
  getFileItems,
  getFileToEdit,
  getIsUploadComplete,
  getIsUploadConfirmed,
} from './files.selectors';
import { FilePrepareResult, PrepareFilesService } from './prepare-files/prepare-files.service';

export const FileCounterGlobal = {
  NEW_FILE_ID: 1,
};

export const STATUS_POLLING_RETRIES = 3;
export const STATUS_POLLING_RETRY_DELAY_MS = 2000;

interface FileItemsPrepareResult {
  fileItems: FileItemFromComputer[];
  failedFilesNotifications: string[];
}

const STATUS_POLLING_INTERVAL_MS = 3 * 1000;
// encoding status request timeout includes retries! (must be greater than time needed for retries)
export const STATUS_POLLING_TIMEOUT_MS = 15 * 1000;

interface StatusPollingResource {
  id: string;
  state: 'Uploading' | 'Encoding';
}

@Injectable()
export class FilesEffects {
  private readonly actions$ = inject(Actions);
  private readonly prepareFilesService = inject(PrepareFilesService);
  private readonly store = inject(Store);
  private readonly upload = inject(UploadApiService);

  emptyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeFile, removeAllFiles),
      concatLatestFrom(() => [this.store.select(getFileItems), this.store.select(getActiveView)]),
      filter(([, fileItems, activeView]) => activeView === 'file-list' && fileItems?.length === 0),
      map(() => switchView({ view: 'source-from-computer' })),
    ),
  );

  prepareFilesFromComputer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(prepareFilesFromComputer),
      concatLatestFrom(() => [
        this.store.select(getAcceptedFileExtensions),
        this.store.select(getIsMultipleSelection),
        this.store.select(getMinimumImageDimensions),
      ]),
      switchMap(([action, acceptedFileTypes, isMultiple, minimumImageDimensions]) =>
        this.prepareFileItems(action.files, acceptedFileTypes, isMultiple, minimumImageDimensions),
      ),
      switchMap(({ fileItems, failedFilesNotifications }) => {
        const actions: Action[] = [];

        if (failedFilesNotifications.length > 0) {
          const notifications: NotificationData[] = failedFilesNotifications.map(
            (message) => ({ type: 'error', message }) as NotificationData,
          );
          actions.push(triggerNotifications({ notifications }));
        }

        if (fileItems.length > 0) {
          actions.push(switchView({ view: 'file-list' }), addFilesFromComputer({ fileItems }));
        }

        return actions;
      }),
    ),
  );

  startUploadFromComputer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addFilesFromComputer),
      map(({ fileItems }) => startUploadTry({ fileItems })),
    ),
  );

  startUploadAfterEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveImageEdit),
      withLatestFrom(this.store.select(getFileToEdit)),
      // filter if upload already initialized (for an unedited file) - no need to start new upload
      filter(([, editedFileItem]) => !editedFileItem?.uploadEdited?.uploadStage),
      map(([, editedFileItem]) => startUpload({ fileItems: [editedFileItem as FileItem] })),
    ),
  );

  startUploadAfterUndo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(undoImageEdit),
      withLatestFrom(this.store.select(getFileToEdit)),
      // no need to start new upload if upload already initialized (for an edited file)
      filter(([, undoedFileItem]) => !undoedFileItem?.upload?.uploadStage),
      map(([, undoedFileItem]) => startUpload({ fileItems: [undoedFileItem as FileItem] })),
    ),
  );

  startUploadFileRetry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fileRetry),
      withLatestFrom(this.store.select(getFileItems)),
      map(([{ fileId }, fileItems]) => fileItems.find((item) => item.id === fileId)),
      filter((fileItem) => !!fileItem),
      map((fileItem) => startUpload({ fileItems: [fileItem as FileItem] })),
    ),
  );

  // Start all not uploading or failed files on user 'Upload' confirmation
  startUploadAfterUploadConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmUpload),
      withLatestFrom(this.store.select(getFileItems)),
      map(([, fileItems]) => fileItems.filter((item) => shouldStartUpload(item))),
      filter((fileItems) => fileItems?.length > 0),
      map((fileItems) => startUpload({ fileItems })),
    ),
  );

  startUploadTry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startUploadTry),
      withLatestFrom(this.store.select(getIsMultipleSelection), this.store.select(getIsCropForced)),
      mergeMap(([{ fileItems }, isMultipleSelection, isCropForced]) =>
        !isMultipleSelection && isCropForced && fileItems?.[0]?.file?.type !== 'image/svg+xml'
          ? [startImageEdit({ fileId: fileItems[0].id }), switchView({ view: 'image-edit' })]
          : [
              startUpload({ fileItems }),
              confirmUpload(), // instantly confirm the upload
            ],
      ),
    ),
  );

  imageEditCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelImageEdit),
      withLatestFrom(this.store.select(getIsCropForced), this.store.select(getFileToEdit)),
      mergeMap(([, isCropForced, editedFile]) => [
        switchView({ view: 'file-list' }),
        // crop is forced and it is the inital cropping
        ...(isCropForced && !editedFile?.cropData && editedFile?.id
          ? [removeFile({ fileId: editedFile.id })]
          : []),
      ]),
    ),
  );

  uploadFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startUpload),
      filter(({ fileItems }) => fileItems?.length > 0),
      concatLatestFrom(() => [
        this.store.select(getAccountId),
        this.store.select(getProjectId),
        this.store.select(getIsSecure),
        this.store.select(getCategory),
      ]),
      mergeMap(([{ fileItems: requestedFileItems }, accountId, projectId, isSecure, category]) => {
        if (!accountId) {
          throw new Error('Account ID is required to start upload.');
        }

        return this.upload
          .startUpload(accountId, requestedFileItems, projectId, isSecure, category)
          .pipe(
            // the startUpload request takes some time, so the most recent fileItems are needed
            concatLatestFrom(() => this.store.select(getFileItems)),
            switchMap(([results, stateFileItems]) =>
              getUploadActions(requestedFileItems, stateFileItems, results),
            ),
            catchError((err) => {
              sentryLog('[Uploader] Error while initializing upload', 'error', {
                error: err instanceof Error ? err.message : String(err),
                status: err?.status,
                fileIds: requestedFileItems.map((item) => item.id),
                accountId,
                projectId,
                category,
              });
              return [
                errorUploads({ fileIds: requestedFileItems.map((item) => item.id) }),
                triggerNotifications({
                  notifications: [
                    {
                      type: 'error',
                      message:
                        'Error while initializing upload. Please check your Internet connection and try again.',
                    },
                  ],
                }),
              ];
            }),
          );
      }),
    ),
  );

  upload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadFile),
      mergeMap((action) =>
        this.upload.upload(action.data, action.url).pipe(
          takeUntil(this.getFileUploadBreak(action.fileId)),
          switchMap((event) => mapUploadEventToActions(event, action.fileId, action.resourceId)),
          catchError((err) => {
            sentryLog('[Uploader] Error while uploading file', 'error', {
              error: err instanceof Error ? err.message : String(err),
              status: err?.status,
              fileId: action.fileId,
              resourceId: action.resourceId,
            });

            return of(
              errorUploads({ fileIds: [action.fileId] }),
              triggerNotifications({
                notifications: [
                  {
                    type: 'error',
                    message:
                      'Error while uploading. Please check your Internet connection and try again.',
                  },
                ],
              }),
            );
          }),
        ),
      ),
    ),
  );

  uploadEnd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(finishUpload),
      mergeMap((action) =>
        this.upload.end([action.resourceId]).pipe(
          takeUntil(this.getFileUploadBreak(action.fileId)),
          map(() => uploadFinished({ resourceId: action.resourceId })),
          catchError((err) => {
            console.error('[Uploader]', err);

            return of(
              errorUploads({ fileIds: [action.fileId] }),
              triggerNotifications({
                notifications: [
                  {
                    type: 'error',
                    message:
                      'Error while finishing upload. Please check your Internet connection and try again.',
                  },
                ],
              }),
            );
          }),
        ),
      ),
    ),
  );

  // Start encoding after upload ends
  uploadEnded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadFinished),
      map(({ resourceId }) => startEncoding({ resourceId })),
    ),
  );

  statusPolling$ = createEffect(
    () =>
      ({
        statusInterval = STATUS_POLLING_INTERVAL_MS,
        statusTimeout = STATUS_POLLING_TIMEOUT_MS,
        retryInterval = STATUS_POLLING_RETRY_DELAY_MS,
        scheduler = asyncScheduler,
      } = {}): Observable<Action> =>
        this.getStatusPollingResources().pipe(
          switchMap((resources) =>
            timer(0, statusInterval, scheduler).pipe(
              takeWhile(() => resources.length > 0),
              map(() => resources),
            ),
          ),
          throttleTime(statusInterval, scheduler),
          exhaustMap((resources) =>
            this.upload.checkResourceStatuses(resources.map((r) => r.id)).pipe(
              switchMap(({ results }) => this.generateStatusActions(resources, results)),
              retryWhen((errors) =>
                errors.pipe(
                  delay(retryInterval, scheduler),
                  take(STATUS_POLLING_RETRIES),
                  scan(statusPollingRetryScan, STATUS_POLLING_RETRIES - 1),
                ),
              ),
              timeout(statusTimeout, scheduler),
              catchError((err) => {
                console.error('[Uploader]', err);

                return of(
                  revokeUploadConfirmation(),
                  triggerNotifications({
                    notifications: [
                      {
                        type: 'error',
                        message:
                          'Could not fetch encoding state. Please check your Internet connection.',
                      },
                    ],
                  }),
                );
              }),
            ),
          ),
        ),
  );

  // user confirms the upload - the effect waits for the uploads to complete
  uploadSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmUpload),
      switchMap(() =>
        this.store.select(getIsUploadComplete).pipe(
          takeUntil(
            // stop if upload gets cancelled
            this.store
              .select(getIsUploadConfirmed)
              .pipe(filter((isConfirmed) => isConfirmed === false)),
          ),
          filter((isUploadComplete) => isUploadComplete),
        ),
      ),
      withLatestFrom(this.store.select(getFileItems)),
      map(([, fileItems]) => confirmFiles({ fileItems })),
    ),
  );

  returnFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmFiles),
      switchMap(({ fileItems }) =>
        this.upload.confirmResources(mapToResourceIds(fileItems)).pipe(
          map(() => mapToFileResults(fileItems)),
          mergeMap((fileResults) => [returnFiles({ fileResults })]),
          catchError((err) => {
            console.error('[Uploader]', err);

            return of(
              revokeUploadConfirmation(),
              triggerNotifications({
                notifications: [
                  {
                    type: 'error',
                    message:
                      'Could not finalize the upload. Please check your Internet connection and try again.',
                  },
                ],
              }),
            );
          }),
        ),
      ),
    ),
  );

  private getStatusPollingResources(): Observable<StatusPollingResource[]> {
    return this.actions$.pipe(
      ofType(
        waitForUpload,
        waitForUploadError,
        uploadFinished,
        startEncoding,
        finishEncoding,
        errorEncoding,
        removeFile,
        removeAllFiles,
      ),
      withLatestFrom(this.store.select(getFileItems)),
      scan(
        (acc, [action, fileItems]) => reduceStatusPollingResources(acc, fileItems, action),
        [] as StatusPollingResource[],
      ),
    );
  }

  private getFileUploadBreak(removedFileId: number): Observable<void> {
    return merge(
      this.actions$.pipe(
        ofType(removeFile),
        filter(({ fileId }) => fileId === removedFileId),
      ),
      this.actions$.pipe(ofType(removeAllFiles)),
      this.actions$.pipe(
        ofType(saveImageEdit),
        withLatestFrom(this.store.select(getFileToEdit)),
        filter(([, editedFileItem]) => editedFileItem?.id === removedFileId),
      ),
      this.actions$.pipe(
        ofType(undoImageEdit),
        filter(({ fileId: undoedFileId }) => undoedFileId === removedFileId),
      ),
    ).pipe(map(() => undefined));
  }

  private prepareFileItems(
    filesToPrepare: File[],
    acceptedFileExtensions: string[],
    isMultiple: boolean,
    minimumImageDimensions?: [number, number],
  ): Observable<FileItemsPrepareResult> {
    // Take one file if no multiple selection possible
    if (!isMultiple) {
      filesToPrepare = [filesToPrepare[0]];
    }

    const preparedFiles$ = filesToPrepare.map((file) =>
      from(
        this.prepareFilesService.prepareFile(file, acceptedFileExtensions, minimumImageDimensions),
      ),
    );

    return forkJoin(preparedFiles$).pipe(
      map((results) => {
        const successfulResults = results.filter(
          (result) => !result.errors || result.errors.length === 0,
        );
        const fileItems = successfulResults.map(createFileItem);

        const failedFilesNotifications: string[] = results
          .filter((result) => result.errors && result.errors.length > 0)
          .map((failedResult) =>
            this.prepareFilesService.getErrorMessage(
              failedResult.file.name,
              failedResult?.errors?.[0],
              acceptedFileExtensions,
              minimumImageDimensions,
            ),
          );

        return { fileItems, failedFilesNotifications };
      }),
    );
  }

  private generateStatusActions(
    resources: StatusPollingResource[],
    results: ResourceResult[],
  ): Action[] {
    return results.reduce((acc, result, index) => {
      const resource = resources[index];
      if (resource.state === 'Uploading') {
        return this.reduceUploadFinish(acc, resource, result);
      } else {
        return this.reduceEncodingStatus(acc, resource, result);
      }
    }, [] as Action[]);
  }

  private reduceUploadFinish(
    actions: Action[],
    resource: StatusPollingResource,
    result: ResourceResult,
  ): Action[] {
    if (result.status === 'Failed') {
      return [
        ...actions,
        waitForUploadError({ resourceId: resource.id }),
        triggerNotifications({
          notifications: [
            {
              type: 'error',
              message:
                'Error while waiting for the upload to complete. Try again or remove the failed resources.',
            },
          ],
        }),
      ];
    } else if (result.status === 'Uploading') {
      return actions;
    } else if (this.prepareFilesService.isSizeValid(result.size)) {
      return [...actions, uploadFinished({ resourceId: resource.id })];
    } else {
      return [
        ...actions,
        waitForUploadError({ resourceId: resource.id }),
        triggerNotifications({
          notifications: [
            {
              type: 'error',
              message: this.prepareFilesService.getErrorMessage(
                result.filename,
                'file-size-exceeded',
                [],
              ),
            },
          ],
        }),
      ];
    }
  }

  private reduceEncodingStatus(
    actions: Action[],
    resource: StatusPollingResource,
    result: ResourceResult,
  ): Action[] {
    switch (result.status) {
      case 'Done':
        return [...actions, finishEncoding({ resourceId: resource.id, resource: result })];
      case 'Converting':
        return [
          ...actions,
          setEncodingProgress({ resourceId: resource.id, progress: result.progressPct || 0 }),
        ];
      case 'Failed':
        console.error(`[Uploader] Error while encoding resource ${resource.id}`);
        return [
          ...actions,
          errorEncoding({ resourceId: resource.id }),
          triggerNotifications({
            notifications: [
              {
                type: 'error',
                message: 'Error while encoding. Try again or remove the failed resources.',
              },
            ],
          }),
        ];
      default:
        return actions;
    }
  }
}

function createFileItem({
  file,
  objectUrl,
  imageWidth,
  imageHeight,
}: FilePrepareResult<File>): FileItemFromComputer {
  const thumbnailUrl = isImage(file.type) ? objectUrl : undefined;

  return {
    id: FileCounterGlobal.NEW_FILE_ID++,
    thumbnailUrl,
    name: file.name,
    source: 'from-computer',
    objectUrl,
    imageWidth,
    imageHeight,
    file,
  };
}

function shouldStartUpload(item: FileItem): boolean {
  const uploadState = getUploadState(item);
  return !uploadState || uploadState.uploadStage === 'ERROR';
}

function getUploadActions(
  requestedFileItems: FileItem[],
  stateFileItems: FileItem[],
  results: UploadStartResult[],
): Action[] {
  return results.reduce((acc, result, index) => {
    const stateFileItem = stateFileItems.find((item) => item.id === requestedFileItems[index].id);

    if (!stateFileItem) {
      // While the upload initialization request was in progress, the file might have been cancelled or edited/undoed
      return acc;
    }

    // Proceed with upload
    return [
      ...acc,
      uploadFile({
        fileId: stateFileItem.id,
        data: getFileToUpload(stateFileItem),
        resourceId: result.id,
        url: result.uploadUrl,
      }),
    ];
  }, [] as Action[]);
}

function mapUploadEventToActions(
  event: HttpEvent<void>,
  fileId: number,
  resourceId: string,
): Action[] {
  switch (event.type) {
    case HttpEventType.UploadProgress: {
      const progress =
        event.total === undefined ? 0 : Math.round((100 * event.loaded) / event.total);
      return [setUploadFileProgress({ resourceId, progress })];
    }

    case HttpEventType.Response:
      return [finishUpload({ fileId, resourceId })];

    default:
      return [];
  }
}

function reduceStatusPollingResources(
  resources: StatusPollingResource[],
  fileItems: FileItem[],
  action: Action & { resourceId?: string },
): StatusPollingResource[] {
  switch (action.type) {
    case waitForUpload.type:
      return [...resources, { id: action.resourceId as string, state: 'Uploading' }];
    case uploadFinished.type:
      return resources.filter(({ id }) => id !== action.resourceId);

    case startEncoding.type:
      return [...resources, { id: action.resourceId as string, state: 'Encoding' }];
    case finishEncoding.type:
    case errorEncoding.type:
    case waitForUploadError.type:
      return resources.filter(({ id }) => id !== action.resourceId);
    case removeFile.type: {
      const existingResourceIds = new Set([
        ...fileItems.map((item) => item.upload?.resourceId),
        ...fileItems.map((item) => item.uploadEdited?.resourceId),
      ]);
      return resources.filter(({ id }) => existingResourceIds.has(id));
    }
    case removeAllFiles.type:
      return [];
    default:
      return resources;
  }
}

function mapToResourceIds(fileItems: FileItem[]): string[] {
  return fileItems.map((item) => getUploadState(item)?.resourceId || '');
}

function mapToFileResults(fileItems: FileItem[]): FileResult[] {
  return fileItems.map(
    (item) =>
      ({
        filename: item.name,
        mimetype: mapToMimeType(getFileToUpload(item), item.name),
        type: getFriendlyFileType((getFileToUpload(item) as File).name || item.name),
        size: getFileToUpload(item)?.size,
        url: getUploadState(item)?.resource?.downloadUrl,
        thumbnail: getUploadState(item)?.resource?.thumbnailUrl,
      }) as FileResult,
  );
}

function getFileToUpload(fileItem: FileItem): File | Blob {
  const fileToUpload = fileItem.cropData ? fileItem.cropData.file : fileItem.file;
  if (!fileToUpload) {
    throw new Error('Missing file to upload');
  }
  return fileToUpload;
}

function statusPollingRetryScan(retriesLeft: number): number {
  if (retriesLeft === 0) {
    throw new Error('Status polling failed after maximum retries');
  } else {
    return retriesLeft - 1;
  }
}
